import { AnimalType } from './animal';
import { DayOfWeek } from './common';
import { DiseaseViewModel, FarmMedicalSupplyViewModel } from './injection';
import { UserViewModel } from './user';

export interface CreateNotificationRulesCommand {
  notificationRules: CreateNotificationRuleCommand[];
}

export interface UpdateNotificationRulesCommand {
  notificationRules: UpdateNotificationRuleCommand[];
}

export interface CreateNotificationRuleCommand {
  ruleType: RuleType;
  ruleData: RuleDataForCreation;
  periodData?: ValidationPeriodDataDto;
  emailSettings: EmailSettings;
}

export interface UpdateNotificationRuleCommand {
  id: string;
  ruleType: RuleType;
  ruleData: RuleDataForCreation;
  periodData?: ValidationPeriodDataDto;
  emailSettings: EmailSettings;
}

export interface EmailSettings {
  userIds: string[];
}

export interface ValidationPeriodDataDto {
  weeksData: WeeksData;
}

export interface NotificationRuleViewModel {
  id: string;
  isDisabled: boolean;

  created: Date;
  updated?: Date;
  lastTimeValidated?: Date;
  /**
   * Only set for LungWeatherCorrelation rules, and the only timestamp they move: the other rule types stamp
   * lastTimeValidated when they fire, which this rule type never touches.
   */
  lastForecastMatchedAt?: Date;

  ruleType: RuleType;
  validationPeriodType: ValidationPeriodType;

  ruleData: RuleData;
  periodData?: ValidationPeriodData;
  emailSettings: NotificationRuleEmailSettingsViewModel;
}

export interface NotificationRuleEmailSettingsViewModel {
  userIds: string[];
  users: UserViewModel[];
}

export interface RuleDataForCreation {
  numRuleData?: NumRuleDataForCreation;
  percentageRuleData?: PercentageRuleData;
  minRegAnimalsRuleData?: MinRegAnimalsRuleData;
  lungWeatherRuleData?: LungWeatherRuleDataForCreation;
}

export interface RuleData {
  numRuleData?: NumRuleDataViewModel;
  percentageRuleData?: PercentageRuleData;
  minRegAnimalsRuleData?: MinRegAnimalsRuleData;
  lungWeatherRuleData?: LungWeatherRuleDataViewModel;
}

export interface MinRegAnimalsRuleData {
  minRegistered: number;
  animalType: AnimalType;
}

export interface PercentageRuleData {
  farmMedicalSupplyId: string;
  farmDiseaseId: string;
  animalType: AnimalType;
  ageFrom: number;
  ageTill: number;
  percentage: number;
  isPregnant?: boolean;
  isLactating?: boolean;
  farmMedicalSupply?: FarmMedicalSupplyViewModel;
  farmDisease?: DiseaseViewModel;
}

export interface NumRuleDataViewModel {
  farmMedicalSupplyId: string;
  farmDiseaseId: string;
  animalType: AnimalType;
  minInjected: number;
  isPregnant?: boolean;
  isLactating?: boolean;
  farmMedicalSupply?: FarmMedicalSupplyViewModel;
  farmDisease?: DiseaseViewModel;
}

export interface NumRuleDataForCreation {
  farmMedicalSupplyId: string;
  farmDiseaseId: string;
  animalType: AnimalType;
  minInjected: number;
  isPregnant?: boolean;
  isLactating?: boolean;
}

/**
 * How close the forecast has to sit to a stored wind pattern before a warning goes out - the only tuning
 * offered for this rule type. The match threshold each setting maps to lives in backend configuration, so it
 * can be retuned from stored data without a frontend release.
 */
export type WindSensitivity = 'Low' | 'Medium' | 'High';

export const windSensitivities: WindSensitivity[] = ['Low', 'Medium', 'High'];

export interface LungWeatherRuleDataForCreation {
  /** Required for this rule type, unlike the others: the feature is per disease per stable. */
  farmDiseaseId: string;
  animalType: AnimalType;
  isPregnant?: boolean;
  isLactating?: boolean;
  /** Administrations in one stable within `lookbackDays` that count as a peak. */
  peakThreshold: number;
  /**
   * The rule's timescale in days: both the span a peak has to fall inside and the days of weather before its
   * onset that the pattern is taken from. One field for both - a burst of treatments and the weather that caused
   * it are one phenomenon on one timescale. This rule type sends no periodData; this is its window.
   */
  lookbackDays?: number;
  forecastDays?: number;
  /** Leave unset for Medium. */
  sensitivity?: WindSensitivity;
}

export interface LungWeatherRuleDataViewModel {
  farmDiseaseId?: string;
  /**
   * Still returned by the API for older rows, but not surfaced or sent back: the medication that counts
   * towards a peak is the one configured on the disease.
   */
  farmMedicalSupplyId?: string;
  animalType: AnimalType;
  isPregnant?: boolean;
  isLactating?: boolean;
  peakThreshold: number;
  lookbackDays: number;
  forecastDays: number;
  sensitivity?: WindSensitivity;
  farmMedicalSupply?: FarmMedicalSupplyViewModel;
  farmDisease?: DiseaseViewModel;
}

/** Mirrors the property initialisers on the backend's LungWeatherRuleData. */
export const lungWeatherRuleDefaults = {
  lookbackDays: 7,
  forecastDays: 8,
  sensitivity: 'Medium' as WindSensitivity
};

export interface ValidationPeriodData {
  weeksData?: WeeksData;
}

export interface WeeksData {
  weekCount: number;
  dayOfWeek: DayOfWeek;
}

export enum ValidationPeriodType {
  EveryWeek = 0,
  Weeks = 1,
  /**
   * Runs on every backend pass and derives its own window from the rule data, so no periodData is sent.
   * Used by LungWeatherCorrelation.
   */
  Daily = 2
}

export enum RuleType {
  PercentageLimit = 0,
  NumLimit = 1,
  MinRegistered = 2,
  LungWeatherCorrelation = 3
}
export { DayOfWeek };
